/**
 * Finds contiguous yellow-highlighted text in reading order using Word.js.
 * Uses getTextRanges (WordApi 1.3+) with space / line breaks as ending marks.
 */

/**
 * Office.js request batch size for loading `font` / `text` on `getTextRanges` pieces.
 * Larger batches = fewer `context.sync()` round-trips on long documents (major Apply Changes cost).
 */
const BATCH_SIZE = 320;

const isYellowHighlight = (highlightColor: string | null | undefined): boolean => {
  if (highlightColor == null || highlightColor === "") {
    return false;
  }
  const c = highlightColor.trim();
  if (c === "Yellow" || c === "yellow") {
    return true;
  }
  return /^#FFFF00$/i.test(c) || /^#ff0$/i.test(c) || /^#ffff00ff$/i.test(c);
};

/** When `font.highlightColor` is empty but Word still shows yellow (desktop quirk). */
const isYellowHighlightIndex = (idx: Word.Range["highlightColorIndex"] | undefined): boolean =>
  idx === "Yellow";

const mergeRangeGroup = (group: Word.Range[]): Word.Range => {
  if (group.length === 0) {
    throw new Error("highlightScanner: empty range group");
  }
  let acc = group[0];
  for (let i = 1; i < group.length; i++) {
    acc = acc.expandTo(group[i]);
  }
  return acc;
};

/**
 * Returns one Word.Range per logical yellow placeholder (merged adjacent yellow word-ranges).
 * Each range has `text` loaded (one sync) so callers can read labels without an extra `sync`.
 * Must be called inside `Word.run` with a synced `body` reference.
 */
export const findLogicalYellowHighlightRanges = async (
  context: Word.RequestContext,
  body: Word.Body
): Promise<Word.Range[]> => {
  const fullRange = body.getRange();
  const pieces = fullRange.getTextRanges([" ", "\r", "\n"], true);
  pieces.load("items");
  await context.sync();

  const items = pieces.items;
  const logicalRanges: Word.Range[] = [];
  let currentGroup: Word.Range[] = [];

  const flushGroup = () => {
    if (currentGroup.length > 0) {
      logicalRanges.push(mergeRangeGroup(currentGroup));
      currentGroup = [];
    }
  };

  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const slice = items.slice(i, i + BATCH_SIZE);
    for (const r of slice) {
      r.load(["text", "font/highlightColor", "highlightColorIndex"]);
    }
    await context.sync();

    for (const r of slice) {
      const text = (r.text ?? "").trim();
      if (text.length === 0) {
        continue;
      }
      const hc = r.font.highlightColor;
      if (isYellowHighlight(hc) || isYellowHighlightIndex(r.highlightColorIndex)) {
        currentGroup.push(r);
      } else {
        flushGroup();
      }
    }
  }

  flushGroup();

  for (const r of logicalRanges) {
    r.load("text");
  }
  await context.sync();

  return logicalRanges;
};

/**
 * Removes navigation highlight from a paragraph in **one** range operation.
 * Word-by-word clearing via `getTextRanges` was leaving partial highlights and a striped/broken UI;
 * clearing the full paragraph range avoids fragmented highlight state (FR-014).
 */
export const clearYellowHighlightsInParagraph = async (
  context: Word.RequestContext,
  paragraph: Word.Paragraph
): Promise<void> => {
  const range = paragraph.getRange();
  try {
    range.font.set({ highlightColor: "" });
  } catch {
    try {
      range.font.highlightColor = "";
    } catch {
      /* ignore */
    }
  }
  try {
    const wide = range as Word.Range & { highlightColorIndex?: string };
    wide.highlightColorIndex = "NoColor" as unknown as Word.Range["highlightColorIndex"];
  } catch {
    /* ignore */
  }
  const removeHighlight = (range as Word.Range & { removeHighlight?: () => void }).removeHighlight;
  if (typeof removeHighlight === "function") {
    try {
      removeHighlight.call(range);
    } catch {
      /* ignore */
    }
  }
  await context.sync();
};
